import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import axios from 'axios'
import nanoid from 'nanoid'
import LogRocket from 'logrocket'

import topicsZorg from './topics-zorg.json'
import topicsRetail from './topics-retail.json'
import topicsMaak from './topics-maak.json'
import topicsAlgemeen from './topics-algemeen.json'
import topicsConsultancy from './topics-consultancy.json'

const topics = {
  ...topicsZorg,
  ...topicsRetail,
  ...topicsMaak,
  ...topicsAlgemeen,
  ...topicsConsultancy
}

Vue.use(Vuex)

// Populate empty answers object based on topics
const initialAnswerObject = {
  impact: 0,
  chance: 0,
  noOpinion: false,
  topicHasResults: false,
  topicIsComplete: false,
  impactComment: null,
  questions: {
    q0: 0,
    q1: 0,
    q2: 0,
    q3: 0,
    q4: 0,
    q5: 0
  }
}
let initialAnswers = {}
for (let topic in topics) {
  initialAnswers[topic] = initialAnswerObject
}

// Define initial state
let initialState = {
  topics,
  progress: 'STARTED',
  sessionId: nanoid(),
  userDetails: {
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    companyId: '',
    phone: '',
    zipCode: '',
    province: ''
  },
  coworkers: 0,
  companyType: 'algemeen',
  companyTypes: ['algemeen'],
  companyTypeInput: '',
  colleagues: [],
  wantsCoaching: false,
  feedback: {
    aanraden: null,
    verwachtingen: null,
    volgendestap: null,
    remarks: '',
    opDeHoogte: false
  },
  answers: initialAnswers,
  version: 'v1',
  visualIndented: false
}


// Vuex Store
export default new Vuex.Store({
  state: initialState,
  getters: {
    scores(state) {
      let scores = {}
      for (let topic in state.topics) {
        if (state.topics.hasOwnProperty(topic)) {
          if (state.answers[topic].noOpinion === false) {
            const sizeCategory = Math.ceil(
              (state.answers[topic].impact + state.answers[topic].chance) / 2
            )
            let sumOfAnswers = 0
            let numberOfAnswers = 0
            for (let key in state.answers[topic].questions) {
              if (state.answers[topic].questions.hasOwnProperty(key) && state.answers[topic].questions[key] !== 0) {
                sumOfAnswers += state.answers[topic].questions[key]
                numberOfAnswers++
              }
            }
            let answerPercentage = 0
            let readyForTopic = null
            let topicHasResults = false
            const maxAnswerValue = 7
            const readinessThresholds = [40, 40, 60, 70, 80]
            if (numberOfAnswers === sizeCategory && numberOfAnswers > 0) {
              const averageAnswer = sumOfAnswers / numberOfAnswers
              answerPercentage = averageAnswer * (100 / maxAnswerValue)
              readyForTopic =
                answerPercentage >= readinessThresholds[sizeCategory - 1]
            }
            if (sizeCategory !== 0 && readyForTopic !== null) {
              topicHasResults = true
            }
            scores[topic] = {
              sizeCategory,
              answerPercentage,
              readyForTopic,
              numberOfAnswers,
              topicHasResults
            }
          } else if (state.answers[topic].noOpinion) {
            scores[topic] = {
              noOpinion: true,
              topicHasResults: true
            }
          }
        }
      }
      return scores
    },
    filteredTopics(state) {
      let result = {}
      let resultspertitle = {}
      for (let key in state.topics) {
        if (state.topics.hasOwnProperty(key)) {
          //check companyTypes
          if ((state.companyTypes.includes(state.topics[key].category)) ||
              (state.topics[key].category === 'algemeen' && state.companyTypes.includes('other'))) {
            //checkmin coworkers
            if (state.topics[key].minCoworkersValue <= state.coworkers || typeof state.topics[key].minCoworkersValue === 'undefined') {
              let topic = state.topics[key];
              if(topic.generaltitle)
                topic.generalorspecifictitle = topic.generaltitle;
              else
                topic.generalorspecifictitle = topic.title;

              if(!resultspertitle[topic.generalorspecifictitle]) {
                //title is new, no conflict, mark title as being added and add key
                resultspertitle[topic.generalorspecifictitle] = { "key": key, "topic": topic };
                result[key] = topic;
              } else {
                //title already exists, replace it if the new topic is more specific i.e. not algemeen
                let currentTopic = resultspertitle[topic.generalorspecifictitle].topic;
                let currentKey = resultspertitle[topic.generalorspecifictitle].key;
                if(currentTopic.category == 'algemeen') {
                  //title already exists, topic is algemeen, it should be replaced
                  //delete the current topic
                  delete result[currentKey];
                  //add the new topic
                  resultspertitle[topic.generalorspecifictitle] = { "key": key, "topic": topic };
                  result[key] = topic;
                }
              }
            }
          }
        }
      }



      return result
    }
  },
  mutations: {
    updateUserDetails(state, userDetails) {
      state.userDetails = userDetails
    },
    updateCompanyTypes(state, { organizations, organizationInput }) {
      let companyTypes = organizations || [];
      if (!companyTypes.includes('algemeen')) {
        companyTypes.unshift('algemeen');
      }
      state.companyTypes = companyTypes;
      state.companyTypeInput = organizationInput;
    },
    /*
    addCompanyType(state, { organization, organizationInput }) {
      if (!state.companyTypes.includes(organization)) {
        state.companyTypes.push(organization);
      }
      if (organization === 'other') {
        state.companyTypeInput = organizationInput;
      }
    },
    removeCompanyType(state, { organization }) {
      const index = state.companyTypes.indexOf(organization);
      if (index !== -1) {
        state.companyTypes.splice(index, 1);
        if (organization === 'other') {
          state.companyTypeInput = '';
        }
      }
    },
    */
    updateCoworkers(state, coworkers) {
      state.coworkers = coworkers
    },
    updateColleagues(state, colleagues) {
      state.colleagues = colleagues
    },
    updateWantsCoaching(state, wantsCoaching) {
      state.wantsCoaching = wantsCoaching
    },
    updateFeedback(state, feedback) {
      state.feedback = feedback
    },
    updateImpact(state, payload) {
      state.answers = {
        ...state.answers,
        [payload.topic]: {
          ...state.answers[payload.topic],
          impact: payload.value
        }
      }
    },
    updateImpactComment(state, payload) {
      state.answers = {
        ...state.answers,
        [payload.topic]: {
          ...state.answers[payload.topic],
          impactComment: payload.value
        }
      }
    },
    updateChance(state, payload) {
      state.answers = {
        ...state.answers,
        [payload.topic]: {
          ...state.answers[payload.topic],
          chance: payload.value
        }
      }
    },
    updateNoOpinion(state, payload) {
      state.answers = {
        ...state.answers,
        [payload.topic]: {
          ...state.answers[payload.topic],
          noOpinion: payload.value
        }
      }
    },
    updateAnswer(state, payload) {
      state.answers = {
        ...state.answers,
        [payload.topic]: {
          ...state.answers[payload.topic],
          questions: {
            ...state.answers[payload.topic].questions,
            [payload.questionKey]: payload.value
          }
        }
      }
    },
    updateTopicCompleteness(state, payload) {
      state.answers = {
        ...state.answers,
        [payload.topic]: {
          ...state.answers[payload.topic],
          topicIsComplete: payload.value
        }
      }
    },
    resetTopicAnswers(state, topic) {
      state.answers = {
        ...state.answers,
        [topic]: initialAnswerObject
      }
    },
    updateProgress(state, progress) {
      state.progress = progress
    },
    setInitialState(state, payload) {
      console.debug("INITIAL_STATE", { state });
      //ensure unique session id
      initialState.sessionId = nanoid();
      //use initial state or given payload
      let newState = payload || initialState;
      state.sessionId = newState.sessionId;
      state.userDetails = newState.userDetails;
      state.answers = newState.answers || initialAnswers;
      state.progress = newState.progress;
      state.coworkers = newState.coworkers;
      state.companyTypes = newState.companyTypes;
      state.companyTypeInput = newState.companyTypeInput;
      state.colleagues = newState.colleagues;
      state.wantsCoaching = newState.wantsCoaching;
      state.version = newState.version
    },
    setVisualIndent(state, visualIndented) {
      state.visualIndented = visualIndented
    }
  },
  actions: {
    updateDatabaseRecord({ state }) {
      //use patch instead of set in order to update the session
      //because there is other info (such as lead follow up) that needs to be kept
      axios
        .patch(`https://unizo-ff-scan.firebaseio.com/resultsv3/${state.sessionId}.json`, {
          sessionId: state.sessionId,
          answers: state.answers,
          userDetails: state.userDetails,
          colleagues: state.colleagues,
          companyTypes: state.companyTypes,
          companyTypeInput: state.companyTypeInput,
          coworkers: state.coworkers,
          feedback: state.feedback,
          wantsCoaching: state.wantsCoaching,
          dateTime: new Date().toISOString(),
          progress: state.progress,
          version: state.version
        })
        .then(response => {
          if (process.env.VUE_APP_MODE === 'production') {
            // Identify LogRocket visitor
            LogRocket.identify(state.sessionId, {
              name: state.userDetails.firstName + ' ' + state.userDetails.lastName
            })

            // Identify GA visitor
            gtag('set', { 'user_id': state.sessionId })
          }
        })
        .catch(error => {
          console.log('Er ging iets fout bij het opslaan van de data: ' + error)
        })
    }
  },
  plugins: [createPersistedState({
    key: 'unizoffscanv3',
    paths: ['userDetails', 'answers', 'sessionId', 'progress', 'version', 'colleagues', 'companyType', 'companyTypes', 'companyTypeInput', 'coworkers', 'wantsCoaching'],
    getState: function (key, storage) {
      var value = storage.getItem(key);
      if(value && value !== 'undefined') {
        let jsonValue = JSON.parse(value);
        //console.log(jsonValue.version);
        //console.log(jsonValue.version=='v1');
        if(jsonValue.version=='v1') {
          console.log('data in use of version ' + jsonValue.version + ', data will be resused. Starting with progress ' + jsonValue.progress);
          return jsonValue;
        }
        else {
          console.log('old data in use of version ' + jsonValue.version + ', data will be ignored');
          return undefined;
        }
      }
      else {
        return undefined
      };
    }
  })]
})
